import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, FileSpreadsheet, Calendar } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { useCashOutflowReport, CashOutflowFilters, DisplayCurrency } from '@/hooks/finance/useCashOutflowReport';
import { CashOutflowKPIs } from './reports/CashOutflowKPIs';
import { CashOutflowCharts } from './reports/CashOutflowCharts';
import { ExpenseCategoryBreakdownCard } from './reports/ExpenseCategoryBreakdownCard';
import { PaymentRequestPerformance } from './reports/PaymentRequestPerformance';
import { CashOutflowTables } from './reports/CashOutflowTables';
import { CurrencySelect } from '@/components/ui/currency-select';
import * as XLSX from 'xlsx';


type TimeRange = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_year' | 'custom';

const CashOutflowReport: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('last_3_months');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [aggregationPeriod, setAggregationPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>('ETB');

  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case 'this_month':
        return {
          dateFrom: format(startOfMonth(now), 'yyyy-MM-dd'),
          dateTo: format(endOfMonth(now), 'yyyy-MM-dd'),
        };
      case 'last_3_months':
        return {
          dateFrom: format(subMonths(now, 3), 'yyyy-MM-dd'),
          dateTo: format(now, 'yyyy-MM-dd'),
        };
      case 'last_6_months':
        return {
          dateFrom: format(subMonths(now, 6), 'yyyy-MM-dd'),
          dateTo: format(now, 'yyyy-MM-dd'),
        };
      case 'last_year':
        return {
          dateFrom: format(subMonths(now, 12), 'yyyy-MM-dd'),
          dateTo: format(now, 'yyyy-MM-dd'),
        };
      case 'custom':
        return {
          dateFrom: customDateFrom || undefined,
          dateTo: customDateTo || undefined,
        };
      default:
        return {};
    }
  };

  const filters: CashOutflowFilters = {
    ...getDateRange(),
    aggregationPeriod,
    displayCurrency,
  };

  const {
    isLoading,
    kpis,
    timeSeriesData,
    categoryBreakdown,
    expenseCategoryBreakdown,
    costGroupBreakdown,
    departmentBreakdown,
    paymentMethodBreakdown,
    statusFunnel,
    processingTimeData,
    vendorAnalysis,
    paymentRequests,
    expenses,
    displayCurrency: activeCurrency,
    convertAmount,
  } = useCashOutflowReport(filters);

  const exportToCSV = () => {
    const rows = [
      ['Cash Outflow Report'],
      [`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`],
      [`Period: ${filters.dateFrom || 'All'} to ${filters.dateTo || 'Present'}`],
      [],
      ['KPIs'],
      ['Total Cash Outflow', kpis.totalCashOutflow],
      ['Pending Outflow', kpis.pendingOutflow],
      ['Avg Processing Days', kpis.avgProcessingDays],
      ['Approval Rate', `${kpis.approvalRate}%`],
      ['Monthly Burn Rate', kpis.monthlyBurnRate],
      [],
      ['Time Series Data'],
      ['Period', 'Expenses', 'Payment Requests', 'Total'],
      ...timeSeriesData.map(d => [d.period, d.expenses, d.paymentRequests, d.total]),
      [],
      ['Category Breakdown'],
      ['Category', 'Amount', 'Count'],
      ...categoryBreakdown.map(c => [c.name, c.value, c.count]),
      [],
      ['Department Breakdown'],
      ['Department', 'Amount', 'Count'],
      ...departmentBreakdown.map(d => [d.name, d.value, d.count]),
    ];

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cash_outflow_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Cash Outflow Report'],
      [`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`],
      [`Period: ${filters.dateFrom || 'All'} to ${filters.dateTo || 'Present'}`],
      [],
      ['KPI', 'Value'],
      ['Total Cash Outflow', kpis.totalCashOutflow],
      ['Pending Outflow', kpis.pendingOutflow],
      ['Avg Processing Days', kpis.avgProcessingDays],
      ['Approval Rate (%)', kpis.approvalRate],
      ['Monthly Burn Rate', kpis.monthlyBurnRate],
      ['Total Transactions', kpis.totalTransactions],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Time Series sheet
    const timeSeriesSheet = XLSX.utils.json_to_sheet(timeSeriesData);
    XLSX.utils.book_append_sheet(wb, timeSeriesSheet, 'Time Series');

    // Category Breakdown
    const categorySheet = XLSX.utils.json_to_sheet(categoryBreakdown);
    XLSX.utils.book_append_sheet(wb, categorySheet, 'By Category');

    // Category Hierarchy Breakdown (Categories → Cost Groups → Cost Positions)
    const hierarchyData: any[] = [];
    expenseCategoryBreakdown.forEach(cat => {
      hierarchyData.push({
        'Level': 'Category',
        'Name': cat.name,
        'Code': cat.code,
        'Amount': cat.value,
        'Transactions': cat.count,
      });
      cat.costGroups.forEach(group => {
        hierarchyData.push({
          'Level': '  Cost Group',
          'Name': `  └ ${group.name}`,
          'Code': group.code,
          'Amount': group.value,
          'Transactions': group.count,
        });
        group.costPositions.forEach(pos => {
          hierarchyData.push({
            'Level': '    Cost Position',
            'Name': `      └ ${pos.name}`,
            'Code': pos.code,
            'Amount': pos.value,
            'Transactions': pos.count,
          });
        });
      });
    });
    const hierarchySheet = XLSX.utils.json_to_sheet(hierarchyData);
    XLSX.utils.book_append_sheet(wb, hierarchySheet, 'By Category Hierarchy');

    // Department Breakdown
    const deptSheet = XLSX.utils.json_to_sheet(departmentBreakdown);
    XLSX.utils.book_append_sheet(wb, deptSheet, 'By Department');

    // Vendor Analysis
    const vendorSheet = XLSX.utils.json_to_sheet(vendorAnalysis);
    XLSX.utils.book_append_sheet(wb, vendorSheet, 'Vendors');

    // Payment Requests
    const prData = paymentRequests.map(pr => ({
      'Request #': pr.request_number,
      'Title': pr.title,
      'Vendor': pr.vendor_name || '',
      'Department': pr.department?.name || '',
      'Amount': pr.total_amount,
      'Status': pr.status,
      'Submitted': pr.submitted_at || '',
      'Paid': pr.paid_at || '',
    }));
    const prSheet = XLSX.utils.json_to_sheet(prData);
    XLSX.utils.book_append_sheet(wb, prSheet, 'Payment Requests');

    // Expenses
    const expData = expenses.map(exp => ({
      'Expense #': exp.expense_number,
      'Description': exp.description,
      'Vendor': exp.vendor_name || '',
      'Category': exp.category?.name || '',
      'Department': exp.department?.name || '',
      'Amount': exp.total_amount,
      'Status': exp.payment_status,
      'Date': exp.expense_date,
    }));
    const expSheet = XLSX.utils.json_to_sheet(expData);
    XLSX.utils.book_append_sheet(wb, expSheet, 'Expenses');

    XLSX.writeFile(wb, `cash_outflow_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Report Filters
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportToExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Time Range</Label>
              <Select value={timeRange} onValueChange={(v: TimeRange) => setTimeRange(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                  <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                  <SelectItem value="last_year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {timeRange === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Aggregation Period</Label>
              <Select 
                value={aggregationPeriod} 
                onValueChange={(v: 'daily' | 'weekly' | 'monthly' | 'quarterly') => setAggregationPeriod(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Display Currency</Label>
              <CurrencySelect
                value={displayCurrency}
                onValueChange={(v) => setDisplayCurrency(v as DisplayCurrency)}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <CashOutflowKPIs kpis={kpis} isLoading={isLoading} displayCurrency={activeCurrency} />

      {/* Tabs for different views */}
      <Tabs defaultValue="outflow" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="outflow">Cash Outflow</TabsTrigger>
          <TabsTrigger value="performance">PR Performance</TabsTrigger>
          <TabsTrigger value="data">Detailed Data</TabsTrigger>
        </TabsList>

        <TabsContent value="outflow" className="space-y-6">
          <ExpenseCategoryBreakdownCard expenseCategoryBreakdown={expenseCategoryBreakdown} />
          
          <CashOutflowCharts
            timeSeriesData={timeSeriesData}
            categoryBreakdown={categoryBreakdown}
            departmentBreakdown={departmentBreakdown}
            paymentMethodBreakdown={paymentMethodBreakdown}
          />
        </TabsContent>

        <TabsContent value="performance">
          <PaymentRequestPerformance
            statusFunnel={statusFunnel}
            processingTimeData={processingTimeData}
            vendorAnalysis={vendorAnalysis}
          />
        </TabsContent>

        <TabsContent value="data">
          <CashOutflowTables
            paymentRequests={paymentRequests}
            expenses={expenses}
            displayCurrency={activeCurrency}
            convertAmount={convertAmount}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CashOutflowReport;
